<template>
  <div class="ticket" :class="[ticket && getStatus ? `ticket-status-${getStatus}` : '', ticket && getStatusReading ? 'ticket-status-reading' : '']">
    <div class="ticket-active-indicator" :class="[{ 'active' : getStatusReading === 'reading' }]" />
    <div class="ticket-first-line">
      <new-messenger-conversation-header :ticket="ticket" :target="'tickets'" />
      <div class="ticket-language">
        <div class="flag-circle">
          <img :src="flag[ticket.lang]">
        </div>
      </div>
    </div>
    <div class="ticket-second-line">
      <div>
        <span class="ticket-messages-unread-indicator" />
        <span class="ticket-second-line-title">
          {{ ticket.title }}
        </span>
      </div>
      <span class="ticket-time">
        {{ getTime }}
      </span>
    </div>
  </div>
</template>

<script>
import NewMessengerConversationHeader from './NewMessengerConversationHeader'
import pl from '~/marketplace-front-core/static/icons/flags/pl.svg'
import de from '~/marketplace-front-core/static/icons/flags/de.svg'
import en from '~/marketplace-front-core/static/icons/flags/en.svg'
import fr from '~/marketplace-front-core/static/icons/flags/fr.svg'
import es from '~/marketplace-front-core/static/icons/flags/es.svg'
import it from '~/marketplace-front-core/static/icons/flags/it.svg'
import nl from '~/marketplace-front-core/static/icons/flags/nl.svg'

export default {
  name: 'NewMessengerTicket',
  components: { NewMessengerConversationHeader },
  props: {
    ticket: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      flag: {
        en,
        pl,
        de,
        fr,
        es,
        it,
        nl
      }
    }
  },
  computed: {
    getTime () {
      return this.ticket.lastMessage ? this.$moment(this.ticket.lastMessage).format('ddd HH:mm') : ''
    },
    getStatus () {
      return this.ticket.status === 'CLOSED' || this.ticket.status === 'ARCHIVED'
        ? 'closed'
        : this.ticket.assignedEmployees.length === 0
          ? 'unassigned'
          : this.ticket.unreadMessages
            ? 'unread'
            : ''
    },
    getStatusReading () {
      return this.ticket.id === parseInt(this.$route.query.ticketId) ? 'reading' : ''
    }
  }
}
</script>

<style lang="scss" scoped>
$ticket-hover-bg-color: #f8f8f8;
$ticket-status-reading-hover-bg-color: #F2F2F2;
$ticket-status-unread-bg-color: white;
$ticket-status-unread-hover-bg-color: #f8f8f8;
$ticket-status-unassigned-bg-color: rgba($primary, 0.05);
$ticket-status-unassigned-hover-bg-color: rgba($primary, 0.11);
$ticket-status-unassigned-avatar-bg-color: rgba($primary, 0.16);
$ticket-status-unassigned-reading-bg-color: rgba($primary, 0.11);

.text-semibold {
  font-weight: 600;
}

.ticket {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;

  &:hover {
    cursor: pointer;
    background-color: $ticket-hover-bg-color;
  }

  &.ticket-status-reading {
    background-color: $ticket-hover-bg-color;

    &:hover {
      background-color: $ticket-status-reading-hover-bg-color;
    }
  }

  &.ticket-status-unassigned {
    background-color: $ticket-status-unassigned-bg-color;
    font-weight: 400;
    &:hover {
      background-color: $ticket-status-unassigned-hover-bg-color;
    }
    :deep(.user-avatar) {
      background-color: $ticket-status-unassigned-avatar-bg-color;
    }
    &.ticket-status-reading {
      background-color: $ticket-status-unassigned-reading-bg-color;
    }
  }

  &.ticket-status-closed {
    color: rgba($text_color, 0.5);
    .ticket-language {
      opacity: 0.5;
    }
  }

  &.ticket-status-unread {
    background-color: $ticket-status-unread-bg-color;

    &:hover {
      background-color: $ticket-status-unread-hover-bg-color;
    }

    .ticket-messages-unread-indicator {
      display: block !important;
    }

    :deep(.ticket-title),
    :deep(.initials),
    .ticket-second-line-title,
    .ticket-time {
      font-weight: 600 !important;
    }

    // :deep(.circle) {
    //   border-color: rgba($primary, 0.06) !important;
    // }
  }

  .ticket-active-indicator {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 2px;
    background-color: $primary;
    visibility: hidden;
    &.active {
      visibility: visible;
    }
  }

  .ticket-first-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    .ticket-users-title-container {
      display: flex;
      align-items: center;
      gap: 10px;
      overflow: hidden;

      .ticket-users {
        display: flex;

        &> :deep(.avatar-container) {
          &:nth-child(1) {
            z-index: 10;
          }

          &:nth-child(2) {
            margin-left: -19px;
            opacity: 0.8;
          }
        }
      }

      .ticket-title {
        flex-shrink: 1;
        font-size: 14px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }

    .ticket-language {
      flex-shrink: 0;
      width: 18px;
      height: 18px;

      .flag-circle {
        width: 18px;
        height: 18px;
        position: relative;
        overflow: hidden;
        border-radius: 50%;

        img {
          display: inline;
          width: auto;
          height: 100%;
        }
      }
    }
  }

  .ticket-second-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-width: 0;
    font-size: 12px;

    &>div {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;

      .ticket-notification-dots {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .ticket-messages-unread-indicator {
        display: none;
        flex-shrink: 0;
        width: 8px;
        height: 8px;
        border-radius: 4px;
        background-color: $primary;
      }

      .ticket-unassigned {
        display: inline-block;
        flex-shrink: 0;
        width: 8px;
        height: 8px;
        border-radius: 4px;
        border: 1.5px solid $primary;
      }

      .ticket-second-line-title {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }

    .ticket-time {
      flex-shrink: 0;
    }
  }
}
</style>
